import { api } from './api';

const MOCK_MODE = import.meta.env.VITE_INTELLIHEALTH_MOCK_DATA === 'true'

/**
 * The API proxy returns reasoning as a JSON string from PostgreSQL.
 * Try to parse it into an object; fall back to the raw value if it's not valid JSON.
 */
export function parseRecommendation(raw: unknown): string | object | null {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw as object;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed !== null) return parsed;
    } catch {
      // Not valid JSON — return the raw markdown string as-is
    }
    return raw;
  }
  return null;
}

export interface RecommendationResponse {
  recommendation?: string | object;
  suggestions?: string[];
  error?: string;
}

export interface IntellihealthSyncResponse {
  success: boolean;
  data?: {
    patientId: string;
    requestId: string;
    baseUrl?: string;
    results: Array<{
      type: string;
      status: string;
      orderId?: string;
      httpStatus?: number;
      reason?: string;
      error?: unknown;
    }>;
  };
  error?: {
    message: string;
    requestId?: string;
    upstreamStatus?: number | null;
  };
  message?: string;
}

export interface IntellihealthMeta {
  status: 'ready' | 'processing' | 'pending_data';
  missing: string[];
}

export interface IntellihealthRecommendationsResponse {
  success: boolean;
  data?: {
    patientId: string;
    requestId: string;
    recommendation: string | object | null;
    recommendations: unknown[];
    meta?: IntellihealthMeta;
  };
  error?: {
    message: string;
    requestId?: string;
    upstreamStatus?: number | null;
  };
  message?: string;
}

// ── Mock Data (dev-only, gated by VITE_INTELLIHEALTH_MOCK_DATA) ─────────────

const MOCK_REASONING = `## Treatment Recommendation

Based on comprehensive review of patient demographics, symptom profile, and laboratory results, the following treatment plan is recommended.

### Clinical Assessment
Patient presents with symptoms consistent with testosterone deficiency including fatigue, decreased libido, and mood changes. Laboratory findings confirm Total Testosterone below therapeutic range at 285 ng/dL (reference: 300-1000 ng/dL).

### Recommended Medications
- **Testosterone Cypionate** 200mg/mL — Inject 1mL intramuscularly once weekly
- **Anastrozole** 0.5mg — Take by mouth twice weekly for estradiol management

### Recommended Laboratory Tests
- Comprehensive Metabolic Panel (CMP)
- PSA (Prostate-Specific Antigen)
- Complete Blood Count with Differential

### Follow-up Plan
- Schedule 6-week follow-up appointment for lab review and dose adjustment
- Monitor hematocrit levels at each follow-up`

function buildMockRecommendationsResponse(patientId: string): IntellihealthRecommendationsResponse {
  return {
    success: true,
    data: {
      patientId,
      requestId: 'mock-req-001',
      recommendation: MOCK_REASONING,
      recommendations: [{
        reasoning: MOCK_REASONING,
      }],
      meta: { status: 'ready', missing: [] },
    },
  }
}

function buildMockSyncResponse(): IntellihealthSyncResponse {
  return {
    success: true,
    data: {
      patientId: 'mock',
      requestId: 'mock-sync-001',
      baseUrl: 'mock://intellihealth',
      results: [{ type: 'intake', status: 'accepted' }],
    },
  }
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

// ── Service ─────────────────────────────────────────────────────────────────

export const aiRecommendationService = {
  /**
   * Trigger backend sync to Aura IntelliHealth.
   * Browser talks to novamed-api; novamed-api talks to aura-ai.
   */
  syncPatientToIntellihealth: async (patientId: string): Promise<IntellihealthSyncResponse> => {
    if (MOCK_MODE) {
      await delay(300)
      return buildMockSyncResponse()
    }
    return api.post<IntellihealthSyncResponse>(`/intellihealth/patients/${patientId}/sync`)
  },

  /**
   * Fetch latest recommendation(s) via novamed-api proxy.
   */
  getPatientRecommendations: async (patientId: string, limit = 5): Promise<IntellihealthRecommendationsResponse> => {
    if (MOCK_MODE) {
      await delay(500)
      return buildMockRecommendationsResponse(patientId)
    }
    const qs = new URLSearchParams({ limit: String(limit) })
    return api.get<IntellihealthRecommendationsResponse>(`/intellihealth/patients/${patientId}/recommendations?${qs.toString()}`)
  },

  /**
   * Trigger on-demand recommendation generation, streaming the result chunk by chunk.
   * Uses native fetch (not axios) for streaming support.
   * @returns { abort } — call abort() to cancel the in-flight request.
   */
  generateRecommendation(
    patientId: string,
    therapy?: string,
    onChunk?: (chunk: string) => void,
    onComplete?: () => void,
    onError?: (error: Error) => void,
  ): { abort: () => void } {
    if (MOCK_MODE) {
      let cancelled = false
      const words = MOCK_REASONING.split(' ')
      ;(async () => {
        try {
          for (let i = 0; i < words.length; i++) {
            if (cancelled) return
            await delay(30)
            onChunk?.((i === 0 ? '' : ' ') + words[i])
          }
          if (!cancelled) onComplete?.()
        } catch (e) {
          if (!cancelled) onError?.(e instanceof Error ? e : new Error(String(e)))
        }
      })()
      return { abort: () => { cancelled = true } }
    }

    const controller = new AbortController()
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/'
    const qs = therapy ? `?therapy=${encodeURIComponent(therapy)}` : ''
    const url = `${API_BASE_URL}intellihealth/patients/${patientId}/generate${qs}`

    ;(async () => {
      try {
        const token = localStorage.getItem('authToken')
        const { getActiveClinicId } = await import('@/contexts/ClinicContext')
        const clinicId = getActiveClinicId()

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(clinicId ? { 'x-clinic-id': clinicId } : {}),
          },
          signal: controller.signal,
        })

        if (!response.ok) {
          const text = await response.text().catch(() => 'Request failed')
          let message = text
          try { const json = JSON.parse(text); message = json.error?.message || json.message || text } catch { /* not JSON */ }
          onError?.(new Error(message))
          return
        }

        const contentType = response.headers.get('content-type') || ''

        if (contentType.includes('text/plain') && response.body) {
          const reader = response.body.getReader()
          const decoder = new TextDecoder()
          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            const chunk = decoder.decode(value, { stream: true })
            if (chunk) onChunk?.(chunk)
          }
          onComplete?.()
        } else {
          // Fallback: JSON response (sync-then-fetch)
          const json = await response.json() as { success?: boolean; data?: { recommendation?: string } }
          const rec = json?.data?.recommendation
          if (rec) onChunk?.(typeof rec === 'string' ? rec : JSON.stringify(rec))
          onComplete?.()
        }
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === 'AbortError') return
        onError?.(e instanceof Error ? e : new Error(String(e)))
      }
    })()

    return { abort: () => controller.abort() }
  },
};
